import { useMemo } from "react";
import { useLegendState as useState } from "@/hooks/useLegendState";
import { 
  Sparkles, Send, Copy, Check, Loader2,
  ChefHat, Mail, FileText, Lightbulb, BookOpen
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { QuickPrompt } from "@/types/aiToolkit";

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Sparkles, ChefHat, Mail, FileText, Lightbulb, BookOpen
};

interface QuickPromptModalProps {
  prompt: QuickPrompt | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onExecute?: (filledPrompt: string, variables: Record<string, string>) => void;
}

export function QuickPromptModal({ 
  prompt, 
  open, 
  onOpenChange, 
  onExecute 
}: QuickPromptModalProps) {
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [isExecuting, setIsExecuting] = useState(false);
  const [copied, setCopied] = useState(false);

  const updateVariable = (id: string, value: string) => {
    setVariables(prev => ({ ...prev, [id]: value }));
  };

  const filledPrompt = useMemo(() => {
    if (!prompt) return "";
    let result = prompt.promptTemplate;
    for (const variable of prompt.variables) {
      const value = variables[variable.id] || variable.defaultValue || `[${variable.name}]`;
      result = result.replace(new RegExp(`\\{\\{${variable.id}\\}\\}`, 'g'), value);
    }
    return result;
  }, [prompt, variables]);

  const allRequiredFilled = useMemo(() => {
    if (!prompt) return false;
    return prompt.variables
      .filter(v => v.required)
      .every(v => variables[v.id]?.trim() || v.defaultValue);
  }, [prompt, variables]);

  const handleExecute = async () => {
    if (!prompt || !onExecute) return;
    setIsExecuting(true);
    try {
      await onExecute(filledPrompt, variables);
      onOpenChange(false);
      setVariables({});
    } finally {
      setIsExecuting(false);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(filledPrompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    onOpenChange(false);
    setVariables({});
  };

  if (!prompt) return null;

  const IconComponent = iconMap[prompt.icon] || Sparkles;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center text-white", prompt.color)}>
              <IconComponent className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="text-lg">{prompt.name}</DialogTitle>
              <DialogDescription className="text-sm">
                {prompt.description}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4 -mr-4">
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <h4 className="text-sm font-medium flex items-center gap-2">
                Fill in the details
                {prompt.variables.some(v => v.required) && (
                  <Badge variant="outline" className="text-xs">* Required</Badge>
                )}
              </h4>
              
              {prompt.variables.map((variable) => (
                <div key={variable.id} className="space-y-1.5">
                  <Label htmlFor={variable.id} className="text-sm">
                    {variable.name}
                    {variable.required && <span className="text-destructive ml-1">*</span>}
                  </Label>
                  {variable.placeholder.length > 50 ? (
                    <Textarea
                      id={variable.id}
                      placeholder={variable.placeholder}
                      value={variables[variable.id] || ""}
                      onChange={(e) => updateVariable(variable.id, e.target.value)}
                      className="min-h-[80px]"
                    />
                  ) : (
                    <Input
                      id={variable.id}
                      placeholder={variable.placeholder}
                      value={variables[variable.id] || ""}
                      onChange={(e) => updateVariable(variable.id, e.target.value)}
                    />
                  )}
                  {variable.description && (
                    <p className="text-xs text-muted-foreground">{variable.description}</p>
                  )}
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium">Preview</h4>
                <Button variant="ghost" size="sm" onClick={handleCopy} className="h-7 text-xs">
                  {copied ? (
                    <>
                      <Check className="h-3 w-3 mr-1" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-3 w-3 mr-1" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
              <div className="bg-muted/50 rounded-lg p-3 text-sm whitespace-pre-wrap border">
                {filledPrompt}
              </div>
            </div>
          </div>
        </ScrollArea>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleExecute} 
            disabled={!allRequiredFilled || isExecuting}
            className={cn(prompt.color, "text-white")}
          >
            {isExecuting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Running...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Run Prompt
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
